import { LiveMedicalMatchBlockedReason, MedicalMatchCreationType, MedicalMatchRerunBlockedReason } from '@/api';

/** Badge label shown on each Q&A card for where its question came from. */
export const MEDICAL_MATCH_CREATION_TYPE_LABEL: Record<MedicalMatchCreationType, string> = {
  [MedicalMatchCreationType.LIVE]: 'Live',
  [MedicalMatchCreationType.STANDARD]: 'Standard',
  [MedicalMatchCreationType.CUSTOM]: 'Custom',
};

/**
 * Badge colours per creation type. Live answers are the user's own ad-hoc
 * questions, so they're picked out in the app's green pill treatment
 * (`bg-fletchers-green-50` + `text-fletchers-green-600`, as used in the header
 * and the selector popups); the pre-run question sets stay neutral grey.
 */
export const MEDICAL_MATCH_CREATION_TYPE_BADGE_CLASS: Record<MedicalMatchCreationType, string> = {
  [MedicalMatchCreationType.LIVE]: 'bg-fletchers-green-50 text-fletchers-green-600',
  [MedicalMatchCreationType.STANDARD]: 'bg-fletchers-gray-90 text-gray-700',
  [MedicalMatchCreationType.CUSTOM]: 'bg-fletchers-gray-90 text-gray-700',
};

/** Hard cap enforced by the composer's `maxLength`. */
export const LIVE_QUESTION_MAX_LENGTH = 500;

/**
 * Backend rejects anything shorter than this (after trimming) with a 422, so
 * the composer counts up to it and holds the Ask button back until it's met.
 */
export const LIVE_QUESTION_MIN_LENGTH = 10;

/**
 * How often the live-processing queue (`medical-match-custom/list-live-processing`)
 * is checked while an answer is outstanding. Sending a question only enqueues a
 * worker job, so this is what tells the panel when the answer is there to fetch.
 * Polling stops entirely once the queue empties.
 */
export const LIVE_PROCESSING_POLL_INTERVAL_MS = 30_000;

/**
 * Answer refresh while the worker still has questions in hand. Only runs then:
 * with an empty queue nothing can arrive that the queue itself wouldn't
 * announce, and a change in the queue triggers its own immediate refresh.
 */
export const LIVE_ANSWER_REFRESH_INTERVAL_MS = 100_000;

/**
 * How many times the answers are pulled when the queue shrinks, while any of
 * the questions just answered still has nothing in them. A pull can come back
 * without the answer — it can share a request that set off before the answer
 * was saved — and once the queue is empty nothing else would pull again.
 */
export const LIVE_ANSWER_REFRESH_ATTEMPTS = 3;

/** Wait between those pulls. */
export const LIVE_ANSWER_REFRESH_RETRY_DELAY_MS = 5_000;

/**
 * Per-case key for the questions last seen in the live-processing queue and when
 * they were last seen there. Persisted so a reload mid-answer still recognises a
 * question leaving as "an answer landed" and pulls the new results in, rather
 * than starting from a blank baseline. The time is how far back the checks look
 * for what settled.
 */
export const LIVE_PROCESSING_QUEUE_STORAGE_PREFIX = 'mermaidd.medicalMatch.liveProcessingQueue.';

/**
 * Slack on how far back we look for the questions that settled when one leaves
 * the queue, as answers to scroll to and failures to report. The look-back
 * otherwise runs to the last poll that still saw them queued; this covers the
 * checks' own time in flight and the gap between reading the queue and
 * recording it. Also the whole look-back when there's no record of when that was.
 */
export const LIVE_SETTLED_WINDOW_MARGIN_SECONDS = 120;

/**
 * How long a live answer the panel has just scrolled to stays marked. The
 * `medical-match-entry-flash` glow in index.css runs for 6s; the extra 200ms
 * keeps the class from being stripped before it has played out.
 */
export const LIVE_ANSWER_FLASH_DURATION_MS = 6_200;

/** Per-case key for the unanswered-question notices and the dismissed ids. */
export const LIVE_QUESTION_NOTICES_STORAGE_PREFIX = 'mermaidd.medicalMatch.liveQuestionNotices.';

/**
 * Per-case cap on both remembered lists, newest kept. Dismissals have to be
 * remembered for as long as the question can come back — a stalled one never
 * leaves the processing list — so nothing ages them out, and this is what stops
 * the record growing without bound on a long-running case.
 */
export const LIVE_QUESTION_NOTICE_LIMIT = 50;

/**
 * Green status bar shown above the composer while the worker is answering.
 * Deliberately uncounted: the queue is polled on an interval, so a question
 * finishing as another is sent would make an exact number jump around.
 */
export const LIVE_QUESTIONS_PROCESSING_MESSAGE = 'Processing your questions...';

/**
 * Notice text for a question the backend came back with as FAILED. Quotes the
 * question so the user knows which one to ask again, and asks for a reword
 * because the question itself is what the backend rejected.
 */
export const getLiveQuestionFailedMessage = (question: string): string =>
  `"${question}" failed. Please reword your question and try again.`;

/**
 * Notice text for a question the backend has given up waiting on
 * (`isStaleInProcessing`). That's a failure too, but it stays in the processing
 * list rather than moving to FAILED, so nothing else would tell the user it is
 * never going to be answered. Nothing was wrong with the question — the worker
 * died — so this one doesn't ask for a reword.
 */
export const getLiveQuestionStalledMessage = (question: string): string => `"${question}" failed. Please retry.`;

/**
 * A 409 carrying `LIVE_MEDMATCH_IN_PROGRESS` from send-live-questions is the
 * backend's duplicate check, not a busy signal — the name is a leftover from
 * when the two shared a code path. A similar question has already been asked,
 * so the same wording would only be turned away again.
 */
export const getLiveQuestionDuplicateMessage = (question: string): string =>
  `"${question}" was already asked. Please reword and try again.`;

export const LIVE_QUESTION_TIPS_TITLE = 'Writing effective questions';

export const LIVE_QUESTION_TIPS = [
  'Be specific about what you want to know, e.g. "Has the client ever suffered from back pain pre-2024?"',
  'Keep questions related to this case and its records.',
];

export const MEDICAL_MATCH_DISCLAIMER = "Mermaidd can make mistakes so it's important to verify answers.";

/**
 * Toast for a copy button whose clipboard write was refused. Said out loud
 * because a silent failure leaves whatever was copied before on the clipboard,
 * ready to be pasted in as if it were the answer.
 */
export const MEDICAL_MATCH_COPY_FAILED_SUMMARY = 'Copy failed';
export const MEDICAL_MATCH_COPY_FAILED_DETAIL = 'The text could not be copied to your clipboard. Please try again.';

export const MEDICAL_MATCH_DISCLAIMER_TOOLTIP =
  'All answers are generated based on the uploaded medical records and may contain inaccuracies. Please use your professional judgement and verify against the original records before relying on any output.';

/**
 * How often the composer and rerun-button availability checks are re-run while
 * the panel is open. Nothing pushes job completion (or a newly uploaded
 * document) to the panel, so without this the button would stay disabled and
 * the composer blocked until the user reloaded. Starting or finishing a rerun
 * invalidates both checks outright, so this only covers changes we didn't
 * cause. Polling is paused while the tab is unfocused.
 */
export const MEDICAL_MATCH_STATUS_POLL_INTERVAL_MS = 100_000;

/** Hover text for the rerun button when the backend allows a rerun. */
export const MEDICAL_MATCH_RERUN_AVAILABLE_TOOLTIP =
  'Re-run Medical Match so the answers cover the records added since the last run.';

/**
 * Shown while a full medmatch job is running — as the notice above the
 * composer, the rerun button's hover text and the reason live questions are
 * blocked, since the same job is what makes all of them unavailable.
 */
export const MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE =
  "Medical Match is processing. You can keep reading the existing answers in the meantime, and you'll be able to ask questions once it's complete.";

/**
 * The case has no full medmatch job, or its live index has gone. Either way a
 * rerun is what turns live questions on, so the notice above the composer
 * reads `LIVE_MEDICAL_MATCH_NOT_ENABLED_MESSAGE`, then a re-run link labelled
 * `LIVE_MEDICAL_MATCH_RERUN_LINK_LABEL`, then `LIVE_MEDICAL_MATCH_RERUN_PROMPT`.
 */
export const LIVE_MEDICAL_MATCH_NOT_ENABLED_MESSAGE = 'Live Medical Match is not turned on for this case yet.';
export const LIVE_MEDICAL_MATCH_RERUN_LINK_LABEL = 'Click here to re-run';
export const LIVE_MEDICAL_MATCH_RERUN_PROMPT = 'and start asking your own questions.';

/**
 * How long the notice's re-run link stays disabled after a rerun is turned
 * away, so repeated clicks don't hammer an endpoint that has just said no.
 */
export const MEDICAL_MATCH_RERUN_RETRY_DELAY_MS = 5_000;

/**
 * The user has asked as many live questions as the backend allows them in a
 * day. Shown as a notice above the composer as well as its blocked reason, and
 * in the toast when a send is turned away for it. No number: the allowance is
 * set per environment on the backend, so any figure here would sooner or later
 * be wrong.
 */
export const LIVE_QUESTION_DAILY_LIMIT_MESSAGE =
  "You've reached today's limit for live questions. You can ask more tomorrow.";

/** Composer message per reason the backend gives for blocking live questions. */
export const LIVE_MEDICAL_MATCH_BLOCKED_MESSAGE: Record<LiveMedicalMatchBlockedReason, string> = {
  [LiveMedicalMatchBlockedReason.FULL_MEDMATCH_IN_PROGRESS]: MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE,
  // Unreachable: getLiveBlockedReason lets this one through, as it reports a
  // similar existing question rather than a reason the composer can't be used.
  [LiveMedicalMatchBlockedReason.LIVE_MEDMATCH_IN_PROGRESS]:
    'A similar question was already asked. Please reword and try again.',
  [LiveMedicalMatchBlockedReason.NO_FULL_MEDMATCH_JOB]: LIVE_MEDICAL_MATCH_NOT_ENABLED_MESSAGE,
  [LiveMedicalMatchBlockedReason.CACHE_MISSING]: LIVE_MEDICAL_MATCH_NOT_ENABLED_MESSAGE,
  [LiveMedicalMatchBlockedReason.DAILY_LIMIT_REACHED]: LIVE_QUESTION_DAILY_LIMIT_MESSAGE,
};

/** Shown while the availability checks haven't reported back yet. */
export const LIVE_MEDICAL_MATCH_CHECKING_MESSAGE = 'Checking live question availability...';

/** Shown when an availability check failed — polling keeps retrying underneath. */
export const LIVE_MEDICAL_MATCH_CHECK_FAILED_MESSAGE =
  'Live question availability could not be checked. Retrying automatically.';

/** Fallback for `canRun: false` arriving without a reason we recognise. */
export const LIVE_MEDICAL_MATCH_UNAVAILABLE_MESSAGE = 'Live questions are currently unavailable.';

/** Banner shown when records have been added since the live index was built. */
export const LIVE_MEDICAL_MATCH_STALE_MESSAGE =
  'New records are not included in live answers yet. Re-run Medical Match to include them.';

/** The stale banner while `can-rerun` is blocked on those records still being processed. */
export const LIVE_MEDICAL_MATCH_STALE_DOCUMENTS_PENDING_MESSAGE =
  'New records are added and are currently being processed. Once their processing is complete, you can re-run the medical match to include them in live answers.';

/** Hover text for the rerun button, per reason the backend gives for blocking it. */
export const MEDICAL_MATCH_RERUN_BLOCKED_TOOLTIP: Record<MedicalMatchRerunBlockedReason, string> = {
  [MedicalMatchRerunBlockedReason.JOB_IN_PROGRESS]: MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE,
  [MedicalMatchRerunBlockedReason.NO_NEW_DOCUMENTS]:
    'No new records have been added since the last Medical Match run, so there is nothing to re-run against.',
  [MedicalMatchRerunBlockedReason.DOCUMENTS_PENDING]:
    'Some records are still being processed. Medical Match can be re-run once they are ready.',
};
